import React, { useState } from "react";
import hosys from "@/api/api";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sparkles,
  Loader2,
  Copy,
  Check,
  RefreshCw,
  FileText,
  ClipboardList,
  ShieldCheck,
  ChefHat,
  BookOpen,
  Wrench,
} from "lucide-react";

const TEMPLATES = [
  {
    id: "corrective_action",
    label: "Acció correctiva",
    icon: ShieldCheck,
    color: "bg-red-50 text-red-600",
    description: "Per a incidències i desviacions",
    fields: [
      { key: "incident_type", label: "Tipus d'incidència", placeholder: "ex: temperatura nevera fora de rang", type: "text" },
      { key: "severity", label: "Gravetat", type: "select", options: ["Baixa", "Mitjana", "Alta", "Crítica"] },
      { key: "context", label: "Context addicional (opcional)", placeholder: "ex: va passar al torn de nit...", type: "textarea" },
    ],
    buildPrompt: (fields) =>
      `Genera una acció correctiva professional per a una incidència de tipus "${fields.incident_type}" amb gravetat ${fields.severity}.
${fields.context ? `Context: ${fields.context}` : ""}
Inclou: 1) Acció correctiva immediata, 2) Mesures preventives, 3) Responsable i termini.
Format: text clar i directe per a un registre intern de restaurant.`,
  },
  {
    id: "cleaning_protocol",
    label: "Protocol de neteja",
    icon: ClipboardList,
    color: "bg-emerald-50 text-emerald-600",
    description: "Procediments de neteja i desinfecció",
    fields: [
      { key: "zone", label: "Zona o equip", placeholder: "ex: cuina, neveres, bany...", type: "text" },
      { key: "frequency", label: "Periodicitat", type: "select", options: ["Diàriament", "Setmanalment", "Mensualment"] },
      { key: "products", label: "Productes disponibles (opcional)", placeholder: "ex: lleixiu, desengrassant...", type: "text" },
    ],
    buildPrompt: (fields) =>
      `Genera un protocol de neteja i desinfecció professional per a: ${fields.zone}.
Periodicitat: ${fields.frequency}.
${fields.products ? `Productes: ${fields.products}` : ""}
Inclou: passos detallats, productes i concentracions recomanades, temps de contacte, EPI necessaris i verificació final.
Format: llista numerada, clara i directe per a treballadors de restaurant.`,
  },
  {
    id: "food_handling",
    label: "Protocol manipulació d'aliments",
    icon: ChefHat,
    color: "bg-orange-50 text-orange-600",
    description: "Bones pràctiques de manipulació",
    fields: [
      { key: "food_type", label: "Tipus d'aliment o procés", placeholder: "ex: carn crua, recepció de mercaderies...", type: "text" },
      { key: "risk", label: "Risc principal (opcional)", placeholder: "ex: contaminació creuada...", type: "text" },
    ],
    buildPrompt: (fields) =>
      `Genera un protocol de manipulació d'aliments per a: ${fields.food_type}.
${fields.risk ? `Risc principal a controlar: ${fields.risk}.` : ""}
Basa't en el Reglament CE 852/2004 i bones pràctiques APPCC.
Inclou: condicions d'emmagatzematge, temperatures, higiene personal, control d'al·lèrgens i punts crítics.
Format: instruccions clares per a treballadors de cuina.`,
  },
  {
    id: "internal_procedure",
    label: "Procediment intern",
    icon: BookOpen,
    color: "bg-blue-50 text-blue-600",
    description: "Instruccions de treball i processos",
    fields: [
      { key: "procedure_name", label: "Nom del procediment", placeholder: "ex: recepció de mercaderies, apertura del local...", type: "text" },
      { key: "responsible", label: "Responsable (opcional)", placeholder: "ex: cuiner, encarregat...", type: "text" },
    ],
    buildPrompt: (fields) =>
      `Genera un procediment intern detallat per a: ${fields.procedure_name}.
${fields.responsible ? `Responsable: ${fields.responsible}.` : ""}
Inclou: objectiu, passos ordenats, controls a realitzar, registres necessaris i freqüència.
Format: document estructurat amb passos clars per a un restaurant.`,
  },
  {
    id: "verification_observations",
    label: "Observacions de verificació",
    icon: FileText,
    color: "bg-violet-50 text-violet-600",
    description: "Per a verificacions APPCC",
    fields: [
      { key: "verification_type", label: "Tipus de verificació", placeholder: "ex: control de temperatures, revisió neteges...", type: "text" },
      { key: "result", label: "Resultat", type: "select", options: ["Correcte", "Incorrecte", "Parcial"] },
      { key: "details", label: "Detalls (opcional)", placeholder: "ex: s'ha detectat que...", type: "textarea" },
    ],
    buildPrompt: (fields) =>
      `Genera les observacions per a una verificació APPCC de tipus "${fields.verification_type}".
Resultat de la verificació: ${fields.result}.
${fields.details ? `Detalls: ${fields.details}` : ""}
Inclou: descripció de l'estat trobat, conformitats/no conformitats i recomanació de seguiment.
Format: text formal per a un registre de verificació oficial.`,
  },
  {
    id: "maintenance_report",
    label: "Informe de manteniment",
    icon: Wrench,
    color: "bg-gray-100 text-gray-600",
    description: "Per a equips i instal·lacions",
    fields: [
      { key: "equipment", label: "Equip o zona", placeholder: "ex: nevera 1, campana extractora...", type: "text" },
      { key: "issue", label: "Problema detectat", placeholder: "ex: no manté la temperatura...", type: "text" },
      { key: "action_taken", label: "Acció realitzada (opcional)", placeholder: "ex: s'ha netejat el condensador...", type: "text" },
    ],
    buildPrompt: (fields) =>
      `Genera un informe de manteniment per a l'equip: ${fields.equipment}.
Problema detectat: ${fields.issue}.
${fields.action_taken ? `Acció realitzada: ${fields.action_taken}.` : ""}
Inclou: descripció tècnica del problema, acció correctiva, estat actual, recomanació de seguiment i si cal servei tècnic extern.
Format: informe tècnic breu i professional per a un restaurant.`,
  },
];

export default function DocumentGenerator() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [generatedText, setGeneratedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const selectTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setFieldValues({});
    setGeneratedText("");
  };

  const handleGenerate = async () => {
    if (!selectedTemplate) return;
    setLoading(true);
    setGeneratedText("");
    try {
      const prompt = selectedTemplate.buildPrompt(fieldValues);
      const SYSTEM = `Ets un expert en gestió de restaurants i seguretat alimentària (APPCC, CE 852/2004).
Genera textos professionals, clars i pràctics en català.
Retorna ÚNICAMENT el text sol·licitat, sense introducció ni explicació addicional.`;
      const response = await hosys.integrations.Core.InvokeLLM({ prompt: `${SYSTEM}\n\n${prompt}` });
      setGeneratedText(typeof response === "string" ? response : JSON.stringify(response));
    } catch {
      setGeneratedText("Error generant el text. Torna-ho a intentar.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canGenerate = selectedTemplate && selectedTemplate.fields
    .filter(f => !f.placeholder?.includes("(opcional)") && !f.label?.includes("opcional"))
    .every(f => fieldValues[f.key]?.trim());

  return (
    <div className="space-y-6">
      <PageHeader
        title="Generador de Documents"
        subtitle="Crea esborranys professionals amb ajuda de la IA"
      />

      {/* Template selector */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {TEMPLATES.map(tmpl => {
          const Icon = tmpl.icon;
          const isActive = selectedTemplate?.id === tmpl.id;
          return (
            <button
              key={tmpl.id}
              onClick={() => selectTemplate(tmpl)}
              className={`text-left p-4 rounded-2xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md ${
                isActive
                  ? "border-[#7B2D3F] bg-[#f9f0f2] shadow-md shadow-[#7B2D3F]/10"
                  : "border-gray-100 bg-white hover:border-gray-200"
              }`}
            >
              <div className={`w-9 h-9 rounded-xl ${tmpl.color} flex items-center justify-center mb-2.5`}>
                <Icon className="w-[18px] h-[18px]" />
              </div>
              <p className={`text-sm font-bold leading-tight mb-0.5 ${isActive ? "text-[#7B2D3F]" : "text-gray-800"}`}>
                {tmpl.label}
              </p>
              <p className="text-[11px] text-gray-400 leading-snug">{tmpl.description}</p>
            </button>
          );
        })}
      </div>

      {/* Form + result */}
      {selectedTemplate && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-xl ${selectedTemplate.color} flex items-center justify-center`}>
              <selectedTemplate.icon className="w-4 h-4" />
            </div>
            <h2 className="font-bold text-gray-900">{selectedTemplate.label}</h2>
          </div>

          {/* Fields */}
          <div className="space-y-3">
            {selectedTemplate.fields.map(field => (
              <div key={field.key}>
                <Label className="text-xs font-semibold text-gray-600 mb-1">{field.label}</Label>
                {field.type === "select" ? (
                  <Select
                    value={fieldValues[field.key] || ""}
                    onValueChange={v => setFieldValues(prev => ({ ...prev, [field.key]: v }))}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {field.options.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.type === "textarea" ? (
                  <Textarea
                    placeholder={field.placeholder}
                    value={fieldValues[field.key] || ""}
                    onChange={e => setFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="h-20 resize-none text-sm"
                  />
                ) : (
                  <Input
                    placeholder={field.placeholder}
                    value={fieldValues[field.key] || ""}
                    onChange={e => setFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="h-9 text-sm"
                  />
                )}
              </div>
            ))}
          </div>

          <Button
            onClick={handleGenerate}
            disabled={!canGenerate || loading}
            className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"
          >
            {loading
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Generant esborrany...</>
              : <><Sparkles className="w-4 h-4" /> Generar esborrany</>
            }
          </Button>

          {/* Generated result */}
          {generatedText && !loading && (
            <div className="space-y-2 pt-2 border-t border-gray-50">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Esborrany generat — editable
                </p>
                <div className="flex gap-1">
                  <button
                    onClick={handleGenerate}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Regenerar"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors text-xs font-medium"
                    title="Copiar"
                  >
                    {copied
                      ? <><Check className="w-3.5 h-3.5 text-emerald-500" /> Copiat!</>
                      : <><Copy className="w-3.5 h-3.5" /> Copiar</>
                    }
                  </button>
                </div>
              </div>
              <Textarea
                value={generatedText}
                onChange={e => setGeneratedText(e.target.value)}
                className="h-64 resize-none text-sm leading-relaxed font-mono"
              />
              <p className="text-[11px] text-gray-400">
                ✏️ Pots editar el text lliurement. La decisió final sempre és teva.
              </p>
            </div>
          )}
        </div>
      )}

      {!selectedTemplate && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#f9f0f2] flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-[#7B2D3F] opacity-70" />
          </div>
          <h3 className="font-bold text-gray-800 mb-1">Selecciona un tipus de document</h3>
          <p className="text-sm text-gray-400 max-w-xs mx-auto">
            L'assistent generarà un esborrany professional que podràs editar i copiar.
          </p>
        </div>
      )}
    </div>
  );
}